/**
 * Weight Distribution Utils
 *
 * Pure functions for automatic weight balancing in A/B test variants.
 *
 * Features:
 * - Sum always equals 100%
 * - Minimum weight per variant = 1%
 * - Priority: top to bottom (lower variants get adjusted first)
 */

/** Minimum weight per variant (1%) */
export const MIN_WEIGHT = 1;

/** Total weight that must be distributed (100%) */
export const TOTAL_WEIGHT = 100;

/**
 * Distribute weights evenly among N variants.
 * Extra weight from rounding goes to first variants.
 *
 * @param count - Number of variants
 * @returns Array of weights that sum to 100
 *
 * @example
 * distributeWeightsEvenly(2) // [50, 50]
 * distributeWeightsEvenly(3) // [34, 33, 33]
 */
export function distributeWeightsEvenly(count: number): number[] {
  if (count <= 0) return [];

  const baseWeight = Math.floor(TOTAL_WEIGHT / count);
  const remainder = TOTAL_WEIGHT - baseWeight * count;

  return Array.from({ length: count }, (_, i) =>
    baseWeight + (i < remainder ? 1 : 0)
  );
}

/**
 * Redistribute weights when one variant's weight changes.
 * Other variants share the remainder proportionally.
 * Respects MIN_WEIGHT for all variants.
 *
 * @param variants - Current variants with weights
 * @param changedIndex - Index of variant being changed
 * @param newWeight - New weight for the changed variant
 * @returns Array of new weights that sum to 100
 *
 * @example
 * redistributeWeights([{weight: 50}, {weight: 50}], 0, 70) // [70, 30]
 */
export function redistributeWeights(
  variants: { weight: number }[],
  changedIndex: number,
  newWeight: number
): number[] {
  const count = variants.length;
  if (count === 0) return [];
  if (count === 1) return [TOTAL_WEIGHT];

  const othersCount = count - 1;
  const minReserved = othersCount * MIN_WEIGHT;

  // Clamp newWeight: at least MIN_WEIGHT, at most (100 - minReserved)
  const maxAllowed = TOTAL_WEIGHT - minReserved;
  const clampedWeight = Math.max(MIN_WEIGHT, Math.min(newWeight, maxAllowed));

  // Distribute remainder among other variants
  const remainder = TOTAL_WEIGHT - clampedWeight;
  const baseWeight = Math.floor(remainder / othersCount);
  let extra = remainder - baseWeight * othersCount;

  return variants.map((_, i) => {
    if (i === changedIndex) return clampedWeight;
    const w = baseWeight + (extra-- > 0 ? 1 : 0);
    return Math.max(w, MIN_WEIGHT);
  });
}

/**
 * Get maximum allowed weight for a variant.
 * Ensures other variants can have at least MIN_WEIGHT each.
 *
 * @param variants - Current variants
 * @param index - Index of variant
 * @returns Maximum weight this variant can have
 *
 * @example
 * getMaxAllowedWeight([{weight: 50}, {weight: 50}], 0) // 99
 */
export function getMaxAllowedWeight(
  variants: { weight: number }[],
  index: number
): number {
  const count = variants.length;
  if (count <= 1) return TOTAL_WEIGHT;

  const othersCount = count - 1;
  const minReserved = othersCount * MIN_WEIGHT;

  return TOTAL_WEIGHT - minReserved;
}
